import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../../../../auth'
import { useGlobalFilter } from '../../../../../../shared/context/GlobalFilterContext'
import { useAccessibleVessels } from '../../../../../../shared/access/accessibleVessels'
import { getAccessibleVessels } from '../../../../core/technicalScopeUtils'
import {
  searchPmsJobsCombined,
  getPmsCommandCenterStats,
  getPmsActionCenterStats,
  getRunningHourCounters,
} from '../../../../core/pms/_requests'
import {getProcurementDashboardV2} from '../../../../../../modules/Procurement/core/_requests'
import { getVesselList } from '../../../../../Management/core/_requests'
import type { Vessel } from '../../../../../Management/core/_models'
import type {
  PmsJobCombinedDto,
  PmsCommandCenterDto,
  PmsActionCenterDto,
  RunningHourCounterDto,
} from '../../../../core/pms/_models'
import type {
  Requisition,
  PurchaseOrder,
  GoodsReceiptListRow,
} from '../../../../../../modules/Procurement/core/_models'
import type { PmsDashboardData, PmsDerivedKpis } from '../types'

export function usePmsDashboard() {
  const { currentUser, roleDetails, isSuperAdmin } = useAuth()
  const { data: accessibleVessels } = useAccessibleVessels()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const roleId = Number((currentUser as any)?.role?.id ?? 0) || 0

  // ── Role‑based vessel scoping ──
  const [allVessels, setAllVessels] = useState<Vessel[]>([])
  const [scopedVesselIds, setScopedVesselIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)

  const selectedVesselId = useMemo(() => {
    if (globalState.vessel?.id != null) return Number(globalState.vessel.id)
    if (roleId === 4 && currentUser?.vessel?.id != null) return Number(currentUser.vessel.id)
    return undefined
  }, [globalState.vessel, roleId, currentUser])

  // Load scoped vessels once on mount
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const vessels = await getVesselList()
        if (cancelled) return
        setAllVessels(vessels)
        const scoped = getAccessibleVessels(
          vessels,
          currentUser,
          isSuperAdmin,
          roleDetails,
          accessibleVessels
        )
        setScopedVesselIds(scoped.map((v: any) => v.id))
      } catch {
        if (!cancelled) setScopedVesselIds([])
      }
    }
    load()
    return () => { cancelled = true }
  }, [currentUser, isSuperAdmin, roleDetails, accessibleVessels])

  const effectiveVesselIds = useMemo<number[] | undefined>(() => {
    if (selectedVesselId != null) return [selectedVesselId]
    if (globalState.company && globalVessels && globalVessels.length > 0) {
      return globalVessels.map((v: any) => Number(v.id))
    }
    if (roleId === 4 && currentUser?.vessel?.id) return [Number(currentUser.vessel.id)]
    if (isSuperAdmin) return undefined
    if (scopedVesselIds.length > 0) return scopedVesselIds
    if (roleId === 6 && !currentUser?.companyGroupAdminId) return undefined
    return undefined
  }, [selectedVesselId, globalState.company, globalVessels, roleId, currentUser, scopedVesselIds, isSuperAdmin])

  // ── Dashboard data state ──
  const [overdueJobs, setOverdueJobs] = useState<PmsJobCombinedDto[]>([])
  const [overdueTotalCount, setOverdueTotalCount] = useState<number>(0)
  const [upcomingJobs, setUpcomingJobs] = useState<PmsJobCombinedDto[]>([])
  const [upcomingTotalCount, setUpcomingTotalCount] = useState<number>(0)
  const [recentPOs, setRecentPOs] = useState<PurchaseOrder[]>([])
  const [recentReqs, setRecentReqs] = useState<Requisition[]>([])
  const [recentGRNs, setRecentGRNs] = useState<GoodsReceiptListRow[]>([])
  const [openPRCount, setOpenPRCount] = useState<number>(0)
  const [openPOCount, setOpenPOCount] = useState<number>(0)
  const [grnPendingCount, setGrnPendingCount] = useState<number>(0)
  const [recentRFQs, setRecentRFQs] = useState<any[]>([])
  const [recentQuotations, setRecentQuotations] = useState<any[]>([])
  const [recentInvoices, setRecentInvoices] = useState<any[]>([])
  const [activeRfqCount, setActiveRfqCount] = useState(0)
  const [pendingQuotationCount, setPendingQuotationCount] = useState(0)
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0)
  const [stats, setStats] = useState<PmsCommandCenterDto | null>(null)
  const [actionCenter, setActionCenter] = useState<PmsActionCenterDto | null>(null)
  const [runningHourCounters, setRunningHourCounters] = useState<RunningHourCounterDto[]>([])

  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    try {
      const vesselIdsParam = effectiveVesselIds
      const singleVesselId = selectedVesselId

      const procurementFilter: {companyId?: number; companyType?: string; vesselId?: number} = {
        ...(singleVesselId ? { vesselId: singleVesselId } : {}),
        ...(globalState.company?.type === 'company_group' ? { companyId: globalState.company.id, companyType: 'GROUP' } : {}),
        ...(globalState.company?.type === 'company_admin' ? { companyId: globalState.company.id, companyType: 'ADMIN' } : {}),
      }

      const results = await Promise.allSettled([
        searchPmsJobsCombined({
          state: 'OVERDUE', page: 0, size: 5, sortBy: 'dueDate', sortDir: 'asc',
          ...(singleVesselId ? { vesselId: singleVesselId } : {}),
        }),
        searchPmsJobsCombined({
          state: 'SCHEDULED_20_PERCENT',
          page: 0, size: 5, sortBy: 'dueDate', sortDir: 'asc',
          dateFilterMode: 'UPCOMING',
          ...(singleVesselId ? { vesselId: singleVesselId } : {}),
        }),
        getProcurementDashboardV2(procurementFilter),
        getPmsCommandCenterStats(vesselIdsParam),
        getPmsActionCenterStats(vesselIdsParam),
        singleVesselId
          ? getRunningHourCounters(singleVesselId)
          : effectiveVesselIds && effectiveVesselIds.length === 1
            ? getRunningHourCounters(effectiveVesselIds[0])
            : Promise.resolve([] as RunningHourCounterDto[]),
      ])

      const [overdueRes, upcomingRes, procurementRes, statsRes, actionRes, countersRes] = results

      if (overdueRes.status === 'fulfilled') {
        let content = overdueRes.value.content || []
        if (singleVesselId == null && effectiveVesselIds != null) {
          const allowed = new Set(effectiveVesselIds)
          content = content.filter((item) => {
            const vId = Number(item.job?.vesselId ?? item.planLine?.vesselId)
            return !vId || allowed.has(vId)
          })
        }
        setOverdueJobs(content.slice(0, 5))
        setOverdueTotalCount(overdueRes.value.totalElements ?? content.length)
      }
      if (upcomingRes.status === 'fulfilled') {
        let content = upcomingRes.value.content || []
        if (singleVesselId == null && effectiveVesselIds != null) {
          const allowed = new Set(effectiveVesselIds)
          content = content.filter((item) => {
            const vId = Number(item.job?.vesselId ?? item.planLine?.vesselId)
            return !vId || allowed.has(vId)
          })
        }
        setUpcomingJobs(content.slice(0, 5))
        setUpcomingTotalCount(upcomingRes.value.totalElements ?? content.length)
      }
      if (procurementRes.status === 'fulfilled') {
        const dashboard = procurementRes.value || {}
        const purchaseOrders = dashboard.purchaseOrders || {}
        const requisitions = dashboard.requisitions || {}
        const goodsReceipts = dashboard.goodsReceipts || {}
        setRecentPOs((purchaseOrders.items || []).map((po: any) => ({
          ...po,
          poNumber: po.purchaseOrderNumber,
          vendor: po.vendorName ? {name: po.vendorName} : undefined,
          totalAmount: po.totalAmount,
        })) as PurchaseOrder[])
        setRecentReqs((requisitions.items || []).map((req: any) => ({
          ...req,
          requisition_code: req.requisitionNumber,
          requiredDeliveryDate: req.requiredBy,
        })) as Requisition[])
        setRecentGRNs((goodsReceipts.items || []).map((grn: any) => ({
          ...grn,
          id: grn.id,
          purchaseOrderId: grn.purchaseOrderId,
          purchaseOrderNumber: grn.purchaseOrderNumber,
          requisitionNo: grn.requisitionNumber,
          status: grn.status,
          receivedDate: grn.receivedDate,
        })) as GoodsReceiptListRow[])
        setOpenPOCount(Number(purchaseOrders.count || 0))
        setOpenPRCount(Number(requisitions.count || 0))
        setGrnPendingCount(Number(goodsReceipts.count || 0))
        setRecentRFQs(dashboard.rfqs?.items || [])
        setRecentQuotations((dashboard.quotations?.items || []).map((quotation: any) => ({
          ...quotation,
          vendor: quotation.vendorName ? {name: quotation.vendorName} : undefined,
        })))
        setRecentInvoices(dashboard.invoices?.items || [])
        setActiveRfqCount(Number(dashboard.rfqs?.count || 0))
        setPendingQuotationCount(Number(dashboard.quotations?.count || 0))
        setPendingInvoiceCount(Number(dashboard.invoices?.count || 0))
      }
      if (statsRes.status === 'fulfilled') setStats(statsRes.value)
      if (actionRes.status === 'fulfilled') setActionCenter(actionRes.value)
      if (countersRes.status === 'fulfilled') setRunningHourCounters(countersRes.value || [])
    } catch {
      // silent — dashboard gracefully shows 0
    } finally {
      setLoading(false)
    }
  }, [effectiveVesselIds, selectedVesselId, globalState.company])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  const data: PmsDashboardData = useMemo(() => ({
    overdueJobs,
    upcomingJobs,
    recentPOs,
    recentReqs,
    recentGRNs,
    recentRFQs,
    recentQuotations,
    recentInvoices,
    stats,
    actionCenter,
    runningHourCounters,
    openPRCount,
    openPOCount,
    grnPendingCount,
    activeRfqCount,
    pendingQuotationCount,
    pendingInvoiceCount,
    overdueTotalCount,
    upcomingTotalCount,
  }), [overdueJobs, upcomingJobs, recentPOs, recentReqs, recentGRNs, recentRFQs, recentQuotations, recentInvoices, stats, actionCenter, runningHourCounters, openPRCount, openPOCount, grnPendingCount, activeRfqCount, pendingQuotationCount, pendingInvoiceCount, overdueTotalCount, upcomingTotalCount])

  const kpis: PmsDerivedKpis = useMemo(() => ({
    overdueJobs: overdueTotalCount || (stats?.overdueJobs ?? overdueJobs.length),
    closedJobs: stats?.closedLast30Days ?? 0,
    totalJobs: stats?.totalJobs ?? 0,
    unplannedOverdue: stats?.unplannedOverdueJobs ?? 0,
    pendingDefectRequests: actionCenter?.pendingDefectRequests ?? 0,
    pendingPostponements: actionCenter?.pendingPostponements ?? 0,
  }), [stats, actionCenter, overdueTotalCount, overdueJobs.length])

  return {
    loading,
    data,
    kpis,
    runningHourCounters,
    refresh: fetchDashboardData,
    userVesselId: selectedVesselId,
    effectiveVesselIds,
  }
}
